import { useRef, useCallback, useEffect, useState } from 'react';

/** 与 Calendar.css .calendar-weekday-row height 一致；表头在滚动容器内 sticky，可见月份检测需从表头下沿算起 */
const CALENDAR_WEEKDAY_ROW_PX = 39;

export interface UseCalendarScrollParams {
  scheduleID: string | undefined;
  selectedYear: number;
  selectedMonth: number;
  onSelectYearMonth: (year: number, month: number) => void;
  isScheduleTabActive: boolean;
  currentDate: Date | null;
  /** 当前拍摄计划的日期已初始化完成前，不要滚动，也不要在视口边缘补月份 */
  currentDateReady?: boolean;
  renderedMonths: Date[];
  startMonth: Date | null;
  endMonth: Date | null;
  calendarDataLoading?: boolean;
  loadPreviousMonths: (count: number, scrollContainer?: HTMLDivElement | null) => void;
  loadNextMonths: (count: number) => void;
}

export const useCalendarScroll = ({
  scheduleID,
  selectedYear,
  selectedMonth,
  onSelectYearMonth,
  isScheduleTabActive,
  currentDate,
  currentDateReady = false,
  renderedMonths,
  startMonth,
  endMonth,
  calendarDataLoading = false,
  loadPreviousMonths,
  loadNextMonths,
}: UseCalendarScrollParams) => {
  const calendarContainerRef = useRef<HTMLDivElement>(null);
  const calendarDaysContainerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentDateRef = useRef<HTMLDivElement>(null);

  const scrollCurrentDateToCenter = useCallback((behavior: ScrollBehavior = 'auto'): boolean => {
    const container = calendarDaysContainerRef.current;
    const cell = currentDateRef.current;
    if (!container || !cell) return false;

    const containerRect = container.getBoundingClientRect();
    const cellRect = cell.getBoundingClientRect();
    const targetScrollTop =
      container.scrollTop +
      (cellRect.top - containerRect.top) -
      (container.clientHeight - cellRect.height) / 2;

    container.scrollTo({ top: Math.max(0, targetScrollTop), behavior });
    return true;
  }, []);

  const handleScroll = useCallback(() => {
    if (!calendarDaysContainerRef.current) return;
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      if (!calendarDaysContainerRef.current) return;
      const container = calendarDaysContainerRef.current;
      const containerRect = container.getBoundingClientRect();
      const firstDateRowAnchorY = containerRect.top + CALENDAR_WEEKDAY_ROW_PX;
      const dayCells = Array.from(container.querySelectorAll('.calendar-day-cell'));
      if (dayCells.length === 0) return;

      let firstCompleteRow: { year: number; month: number; day: number } | null = null;

      for (let rowStart = 0; rowStart < dayCells.length; rowStart += 7) {
        const rowEnd = Math.min(rowStart + 7, dayCells.length);
        const rowCells = dayCells.slice(rowStart, rowEnd);
        if (rowCells.length === 0) continue;
        const firstCell = rowCells[0] as HTMLElement;
        if (!firstCell) continue;
        const cellRect = firstCell.getBoundingClientRect();
        const rowTop = cellRect.top;
        if (rowTop >= firstDateRowAnchorY - 5 && rowTop <= firstDateRowAnchorY + 10) {
          const year = parseInt(firstCell.getAttribute('data-year') || '0');
          const month = parseInt(firstCell.getAttribute('data-month') || '0');
          const day = parseInt(firstCell.getAttribute('data-day') || '0');
          if (year && month && day) {
            firstCompleteRow = { year, month, day };
            break;
          }
        }
      }

      if (!firstCompleteRow) {
        for (let rowStart = 0; rowStart < dayCells.length; rowStart += 7) {
          const rowEnd = Math.min(rowStart + 7, dayCells.length);
          const rowCells = dayCells.slice(rowStart, rowEnd);
          if (rowCells.length === 0) continue;
          const firstCell = rowCells[0] as HTMLElement;
          if (!firstCell) continue;
          const cellRect = firstCell.getBoundingClientRect();
          const rowTop = cellRect.top;
          if (rowTop > firstDateRowAnchorY && rowTop <= firstDateRowAnchorY + 500) {
            const year = parseInt(firstCell.getAttribute('data-year') || '0');
            const month = parseInt(firstCell.getAttribute('data-month') || '0');
            const day = parseInt(firstCell.getAttribute('data-day') || '0');
            if (year && month && day) {
              firstCompleteRow = { year, month, day };
              break;
            }
          }
        }
      }

      if (!firstCompleteRow) return;
      const { year, month } = firstCompleteRow;

      if (year !== selectedYear || month !== selectedMonth) {
        onSelectYearMonth(year, month);
      }
    }, 50);
  }, [selectedYear, selectedMonth, onSelectYearMonth]);

  // 滚动监听
  useEffect(() => {
    const container = calendarDaysContainerRef.current;
    if (!container) return;
    const scrollHandler = () => handleScroll();
    container.addEventListener('scroll', scrollHandler, { passive: true });
    return () => {
      container.removeEventListener('scroll', scrollHandler);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [handleScroll]);

  // 初次定位完成前不在视口边缘补月份，避免和「滚到当前日期」抢 scrollTop
  const edgeLoadArmedRef = useRef(false);
  const [edgeLoadEpoch, setEdgeLoadEpoch] = useState(0);

  const armEdgeLoad = useCallback(() => {
    if (edgeLoadArmedRef.current) return;
    edgeLoadArmedRef.current = true;
    setEdgeLoadEpoch((n) => n + 1);
  }, []);

  // Intersection Observer：监听边界，自动加载更多月份
  useEffect(() => {
    if (!edgeLoadArmedRef.current) return;
    const container = calendarDaysContainerRef.current;
    if (!container || renderedMonths.length === 0) return;

    let loadingPrevious = false;
    let loadingNext = false;
    let observer: IntersectionObserver | null = null;

    const setupObserver = () => {
      const firstMonth = renderedMonths[0];
      const lastMonth = renderedMonths[renderedMonths.length - 1];
      if (!firstMonth || !lastMonth) return;

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const element = entry.target as HTMLElement;
              const isFirstMonth = element.dataset.isFirstMonth === 'true';
              const isLastMonth = element.dataset.isLastMonth === 'true';
              if (isFirstMonth && !loadingPrevious) {
                loadingPrevious = true;
                loadPreviousMonths(2, container);
                setTimeout(() => {
                  loadingPrevious = false;
                }, 2000);
              }
              if (isLastMonth && !loadingNext) {
                loadingNext = true;
                loadNextMonths(2);
                setTimeout(() => {
                  loadingNext = false;
                }, 2000);
              }
            }
          });
        },
        { root: container, rootMargin: '300px', threshold: 0.1 }
      );

      const firstYear = firstMonth.getFullYear();
      const firstMonthNum = firstMonth.getMonth() + 1;
      const firstDayCell = container.querySelector(
        `.calendar-day-cell[data-year="${firstYear}"][data-month="${firstMonthNum}"][data-day="1"]`
      ) as HTMLElement;
      if (firstDayCell) {
        firstDayCell.dataset.isFirstMonth = 'true';
        observer.observe(firstDayCell);
      }

      const lastYear = lastMonth.getFullYear();
      const lastMonthNum = lastMonth.getMonth() + 1;
      const daysInLastMonth = new Date(lastYear, lastMonthNum, 0).getDate();
      const lastDayCell = container.querySelector(
        `.calendar-day-cell[data-year="${lastYear}"][data-month="${lastMonthNum}"][data-day="${daysInLastMonth}"]`
      ) as HTMLElement;
      if (lastDayCell) {
        lastDayCell.dataset.isLastMonth = 'true';
        observer.observe(lastDayCell);
      }
    };

    const timer = setTimeout(setupObserver, 500);
    return () => {
      clearTimeout(timer);
      if (observer) {
        observer.disconnect();
      }
    };
  }, [renderedMonths, loadPreviousMonths, loadNextMonths, edgeLoadEpoch]);

  // 进入/回到拍摄计划页时定位到当前日期；日历范围或数据就绪后需重新居中（避免先滚后换月导致停在一半）
  const hasInitialScrolledRef = useRef(false);
  const prevScheduleIDRef = useRef<string | undefined>(scheduleID);
  const prevIsScheduleTabActiveRef = useRef(isScheduleTabActive);
  const prevCalendarRangeKeyRef = useRef('');
  const prevCalendarDataLoadingRef = useRef(calendarDataLoading);

  const disarmEdgeLoad = () => {
    hasInitialScrolledRef.current = false;
    if (!edgeLoadArmedRef.current) return;
    edgeLoadArmedRef.current = false;
    setEdgeLoadEpoch((n) => n + 1);
  };

  if (prevScheduleIDRef.current !== scheduleID) {
    prevScheduleIDRef.current = scheduleID;
    disarmEdgeLoad();
  }

  const calendarRangeKey = `${startMonth?.getTime() ?? 'x'}-${endMonth?.getTime() ?? 'x'}`;
  if (prevCalendarRangeKeyRef.current && prevCalendarRangeKeyRef.current !== calendarRangeKey) {
    disarmEdgeLoad();
  }
  prevCalendarRangeKeyRef.current = calendarRangeKey;

  if (!prevIsScheduleTabActiveRef.current && isScheduleTabActive) {
    disarmEdgeLoad();
  }
  prevIsScheduleTabActiveRef.current = isScheduleTabActive;

  if (prevCalendarDataLoadingRef.current && !calendarDataLoading) {
    disarmEdgeLoad();
  }
  prevCalendarDataLoadingRef.current = calendarDataLoading;

  useEffect(() => {
    if (
      !currentDateReady ||
      !isScheduleTabActive ||
      !currentDate ||
      renderedMonths.length === 0 ||
      calendarDataLoading
    ) {
      return;
    }
    if (hasInitialScrolledRef.current) return;

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let raf2 = 0;

    const finishInitialScroll = () => {
      hasInitialScrolledRef.current = true;
      armEdgeLoad();
    };

    const runScroll = (attempt = 0) => {
      if (cancelled || hasInitialScrolledRef.current) return;

      if (scrollCurrentDateToCenter('auto')) {
        finishInitialScroll();
        return;
      }

      if (attempt < 8) {
        retryTimer = setTimeout(() => runScroll(attempt + 1), 80);
      } else {
        finishInitialScroll();
      }
    };

    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (!cancelled) runScroll(0);
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [
    currentDateReady,
    isScheduleTabActive,
    currentDate,
    renderedMonths,
    startMonth,
    endMonth,
    calendarDataLoading,
    scrollCurrentDateToCenter,
    armEdgeLoad,
  ]);

  return {
    calendarContainerRef,
    calendarDaysContainerRef,
    currentDateRef,
  };
};
